import { PrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

const ieegPreset = definePreset(Aura, {
  semantic: {
    primitive: {
      borderRadius: {
        none: '0',
        xs: '2px',
        sm: '4px',
        md: '6px',
        lg: '8px',
        xl: '12px',
      },
    },
    colorScheme: {
      light: {
        formField: {
          background: '#f7fafc',
          borderColor: '#cad5e0',
          color: '{surface.800}',
          placeholderColor: '{surface.500}',
        },
      },
      dark: {
        // `color` y `placeholderColor` van explícitos: por defecto Aura los
        // deriva de `surface.0`, y basta que esa variable cambie para que el
        // texto escrito en los campos se vuelva ilegible sobre fondo oscuro.
        formField: {
          background: '{surface.900}',
          borderColor: '{surface.600}',
          color: '{surface.200}',
          placeholderColor: '{surface.400}',
        },
        // Tablas, tarjetas, paneles y diálogos. Aura los apoya en `surface.900`,
        // que aquí es justo el fondo de página: las tablas se fundían con él en
        // lugar de elevarse. Un paso por encima les devuelve la jerarquía.
        content: {
          background: '{surface.800}',
          hoverBackground: '{surface.700}',
          borderColor: '{surface.700}',
          color: '{surface.200}',
          hoverColor: '{surface.100}',
        },
        // Sin esto el texto de todos los componentes es blanco puro, que sobre
        // superficie oscura deslumbra en lecturas largas.
        text: {
          color: '{surface.200}',
          hoverColor: '{surface.100}',
          mutedColor: '{surface.400}',
          hoverMutedColor: '{surface.300}',
        },
        // Este token hace doble papel: es el relleno de los botones sólidos y,
        // a la vez, el color de TEXTO de los de contorno y de enlace. Se queda
        // en `primary.400`, que sobre fondo oscuro se lee a 5.7:1; el relleno
        // del botón sólido se oscurece aparte, en `components.button`.
        primary: {
          color: '{primary.400}',
          contrastColor: '{primary.950}',
          hoverColor: '{primary.300}',
          activeColor: '{primary.500}',
        },
      },
    },
  },
  components: {
    button: {
      colorScheme: {
        dark: {
          // El relleno del botón sólido va un paso más oscuro que el acento
          // general: con `primary.400` el botón era lo más brillante de la
          // pantalla. Aquí pesa como acción principal sin gritar.
          root: {
            primary: {
              background: '{primary.500}',
              hoverBackground: '{primary.400}',
              activeBackground: '{primary.600}',
              borderColor: '{primary.500}',
              hoverBorderColor: '{primary.400}',
              activeBorderColor: '{primary.600}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
            },
          },
        },
      },
      root: {
        paddingX: '0.75rem',
        paddingY: '0.5rem',
        sm: {
          fontSize: '0.8em',
          paddingX: '0.6rem',
          paddingY: '0.3rem',
        },
        lg: {
          fontSize: '1.125rem',
          paddingX: '0.6rem',
          paddingY: '0.7rem',
        },
        label: {
          fontWeight: '500',
        },
      },
    },
    inputtext: {
      root: {
        paddingX: '0.75rem',
        paddingY: '0.5rem',
      },
    },
    message: {
      colorScheme: {
        light: {
          info: {
            borderColor: '{primary.200}',
          },
          success: {
            borderColor: '{green.200}',
          },
          warn: {
            borderColor: '{yellow.200}',
          },
          error: {
            borderColor: '{red.200}',
          },
          secondary: {
            borderColor: '{surface.200}',
          },
          contrast: {
            borderColor: '{surface.900}',
          },
        },
        dark: {
          info: {
            borderColor: '{primary.800}',
          },
          success: {
            borderColor: '{green.800}',
          },
          warn: {
            borderColor: '{yellow.800}',
          },
          error: {
            borderColor: '{red.800}',
          },
          secondary: {
            borderColor: '{surface.800}',
          },
          contrast: {
            borderColor: '{surface.200}',
          },
        },
      },
    },
    panel: {
      header: {
        borderColor: 'transparent',
        padding: ' 1rem 1rem 0.5rem 1rem',
      },
      content: {
        padding: '0.5rem 1rem 1rem 1rem',
      },
      colorScheme: {
        light: {
          root: {
            borderColor: '{slate.100}',
          },
          header: {
            background: 'transparent',
            color: '{slate.800}',
          },
        },
        dark: {
          root: {
            borderColor: 'transparent',
          },
          header: {
            background: 'transparent',
            color: '{slate.100}',
          },
        },
      },
    },
    select: {
      root: {
        paddingY: '0.5rem',
      },
    },
    textarea: {
      root: {
        paddingX: '0.75rem',
        paddingY: '0.5rem',
      },
    },
    togglebutton: {
      root: {
        padding: '0.5rem 0.875rem',
        fontWeight: '400',
      },
      colorScheme: {
        light: {
          root: {
            background: '#f7fafc',
            borderColor: '#cad5e0',
            checkedBorderColor: '#cad5e0',
            hoverBackground: '{slate.200}',
            checkedBackground: '{slate.100}',
            checkedColor: '{slate.800}',
          },
        },
        dark: {
          root: {
            background: '#475569',
            borderColor: 'transparent',
            checkedBorderColor: 'transparent',
            hoverBackground: '{slate.700}',
            checkedBackground: '{slate.800}',
            checkedColor: '{slate.100}',
          },
        },
      },
    },
    tooltip: {
      root: {
        padding: '0.25rem 0.5rem',
        maxWidth: '20rem',
      },
    },
  },
});

export const setupPrimeNGConfig = (primengConfig: PrimeNG) => {
  primengConfig.ripple.set(true);
  primengConfig.theme.set({
    preset: ieegPreset,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark',
      cssLayer: {
        name: 'primeng',
        order: 'base, primeng, utilities',
      },
    },
  });
  primengConfig.setTranslation({
    accept: 'Aceptar',
    reject: 'Rechazar',
    cancel: 'Cancelar',
    pending: 'Pendiente',
    choose: 'Elegir',
    apply: 'Aplicar',
    dayNames: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
    dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
    dayNamesMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
    monthNames: [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ],
    monthNamesShort: [
      'Ene',
      'Feb',
      'Mar',
      'Abr',
      'May',
      'Jun',
      'Jul',
      'Ago',
      'Sep',
      'Oct',
      'Nov',
      'Dic',
    ],
    today: 'Hoy',
    clear: 'Limpiar',
    dateFormat: 'dd/mm/yy',
    weekHeader: 'Sm',
  });
};
